import { Combobox } from '@shared/components/ui/AdvancedFilter/components';
import { Button } from '@shared/components/ui/button';
import { DatePicker } from '@shared/components/ui/date-picker';
import { Alert, AlertDescription, AlertTitle } from '@shared/components/ui/shadcn/alert';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle
} from '@shared/components/ui/shadcn/dialog';
import { Label } from '@shared/components/ui/shadcn/label';
import { useToast } from '@shared/hooks/useToast';
import { format } from 'date-fns';
import { CheckCircle2, Info, Loader2 } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useClientsDropdown, useCreateAdvanceItems } from '../../hooks';
import { CreateAdvanceItemsResponse } from '../../models';

interface CreateAdvanceButtonProps {
    isOpen: boolean;
    onClose: () => void;
}

export const CreateAdvanceButton = ({ isOpen, onClose }: CreateAdvanceButtonProps) => {
    const { t } = useTranslation('codPayouts');
    const { createAdvanceItemsAsync, isCreating } = useCreateAdvanceItems();
    const { toast } = useToast();
    const { clients, isLoading: isLoadingClients } = useClientsDropdown();

    const [clientId, setClientId] = useState('');
    const [dateFrom, setDateFrom] = useState<Date | undefined>(undefined);
    const [dateTo, setDateTo] = useState<Date | undefined>(undefined);
    const [result, setResult] = useState<CreateAdvanceItemsResponse | null>(null);

    const handleCreateAdvances = async () => {
        try {
            // Construir payload con filtros opcionales
            const payload: {
                client_id?: number;
                date_from?: string;
                date_to?: string;
            } = {};

            if (clientId) {
                payload.client_id = parseInt(clientId, 10);
            }

            if (dateFrom) {
                payload.date_from = format(dateFrom, 'yyyy-MM-dd');
            }

            if (dateTo) {
                payload.date_to = format(dateTo, 'yyyy-MM-dd');
            }

            const resultData = await createAdvanceItemsAsync(payload);
            setResult(resultData);
            setClientId('');
            setDateFrom(undefined);
            setDateTo(undefined);
        } catch (error: any) {
            toast({
                title: t('advance.error_title', 'Error al crear anticipos'),
                description: error.message || t('advance.error_description', 'No se pudieron crear los anticipos'),
                variant: 'destructive',
            });
        }
    };

    // Reset form when modal closes
    useEffect(() => {
        if (!isOpen) {
            setClientId('');
            setDateFrom(undefined);
            setDateTo(undefined);
            setResult(null);
        }
    }, [isOpen]);

    const handleClose = () => {
        if (!isCreating) {
            onClose();
        }
    };

    // Convertir clientes a formato de opciones para Combobox
    const clientOptions = useMemo(
        () =>
            clients.map((client) => ({
                label: client.name,
                value: String(client.id),
            })),
        [clients]
    );

    return (
        <Dialog open={isOpen} onOpenChange={handleClose}>
            <DialogContent className="sm:max-w-[500px]">
                <DialogHeader>
                    <DialogTitle className="text-foreground">
                        {t('advance.modal_title', 'Crear Anticipos Automáticos')}
                    </DialogTitle>
                    <DialogDescription className="text-muted-foreground">
                        {t(
                            'advance.modal_description',
                            'Se generarán automáticamente items de anticipo para todos los pedidos entregados elegibles sin liquidar. Puedes aplicar filtros opcionales.'
                        )}
                    </DialogDescription>
                </DialogHeader>

                {result ? (
                    <div className="space-y-4">
                        <Alert variant={result.items_created > 0 ? 'default' : 'default'}>
                            {result.items_created > 0 ? (
                                <CheckCircle2 className="h-4 w-4" />
                            ) : (
                                <Info className="h-4 w-4" />
                            )}
                            <AlertTitle className="text-foreground">
                                {result.items_created > 0
                                    ? t('advance.success_title', 'Anticipos creados')
                                    : t('advance.info_title', 'Información')}
                            </AlertTitle>
                            <AlertDescription className="text-muted-foreground">
                                <div className="space-y-2 mt-2">
                                    <div className="flex justify-between items-center">
                                        <span>Pedidos procesados:</span>
                                        <span className={`font-medium ${result.orders_processed > 0 ? 'text-success' : 'text-muted-foreground'}`}>
                                            {result.orders_processed}
                                        </span>
                                    </div>
                                    {result.items_created > 0 && (
                                        <div className="flex justify-between items-center">
                                            <span>Items de anticipo creados:</span>
                                            <span className="font-medium text-success">
                                                {result.items_created}
                                            </span>
                                        </div>
                                    )}
                                    <div className="flex justify-between items-center">
                                        <span>Clientes procesados:</span>
                                        <span className={`font-medium ${result.clients_processed > 0 ? 'text-success' : 'text-muted-foreground'}`}>
                                            {result.clients_processed}
                                        </span>
                                    </div>
                                </div>
                            </AlertDescription>
                        </Alert>

                        <DialogFooter>
                            <Button
                                type="button"
                                onClick={() => {
                                    setResult(null);
                                    setClientId('');
                                    setDateFrom(undefined);
                                    setDateTo(undefined);
                                    onClose();
                                }}
                            >
                                {t('advance.close', 'Cerrar')}
                            </Button>
                        </DialogFooter>
                    </div>
                ) : (
                    <form onSubmit={(e) => { e.preventDefault(); handleCreateAdvances(); }} className="space-y-4">
                        {/* Cliente (opcional) */}
                        <div className="space-y-2">
                            <Label htmlFor="client" className="text-foreground">
                                {t('filters.client', 'Cliente')} <span className="text-muted-foreground text-xs">({t('common.optional', 'Opcional')})</span>
                            </Label>
                            <Combobox
                                options={clientOptions}
                                placeholder={
                                    isLoadingClients
                                        ? t('advance.loading_clients', 'Cargando clientes...')
                                        : t('filters.all_clients', 'Buscar cliente...')
                                }
                                value={clientId}
                                setValue={setClientId}
                                disabled={isCreating || isLoadingClients}
                                translations={{
                                    notFound: t('advance.no_clients_found', 'No se encontraron clientes'),
                                }}
                            />
                            {clientId && (
                                <Button
                                    type="button"
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => setClientId('')}
                                    className="h-auto p-0 text-xs text-muted-foreground hover:text-foreground"
                                >
                                    {t('filters.clear', 'Limpiar filtro')}
                                </Button>
                            )}
                        </div>

                        {/* Fecha Desde (opcional) */}
                        <div className="space-y-2">
                            <Label htmlFor="date-from" className="text-foreground">
                                {t('filters.date_from', 'Fecha Desde')} <span className="text-muted-foreground text-xs">({t('common.optional', 'Opcional')})</span>
                            </Label>
                            <DatePicker
                                date={dateFrom}
                                onDateChange={setDateFrom}
                                placeholder={t('filters.select_date', 'Seleccionar fecha')}
                                disabled={isCreating}
                            />
                        </div>

                        {/* Fecha Hasta (opcional) */}
                        <div className="space-y-2">
                            <Label htmlFor="date-to" className="text-foreground">
                                {t('filters.date_to', 'Fecha Hasta')} <span className="text-muted-foreground text-xs">({t('common.optional', 'Opcional')})</span>
                            </Label>
                            <DatePicker
                                date={dateTo}
                                onDateChange={setDateTo}
                                placeholder={t('filters.select_date', 'Seleccionar fecha')}
                                disabled={isCreating}
                            />
                        </div>

                        <div className="bg-muted/50 border border-border rounded-md p-3">
                            <p className="text-xs text-muted-foreground">
                                {t(
                                    'advance.modal_info',
                                    'Si no se aplican filtros, se procesarán todos los pedidos entregados elegibles.'
                                )}
                            </p>
                        </div>

                        <DialogFooter>
                            <Button
                                type="button"
                                variant="outline"
                                onClick={handleClose}
                                disabled={isCreating}
                            >
                                {t('advance.cancel', 'Cancelar')}
                            </Button>
                            <Button
                                type="submit"
                                disabled={isCreating}
                            >
                                {isCreating && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
                                {isCreating
                                    ? t('advance.creating', 'Procesando...')
                                    : t('advance.confirm', 'Crear Anticipos')}
                            </Button>
                        </DialogFooter>
                    </form>
                )}
            </DialogContent>
        </Dialog>
    );
};
